import { atom } from 'jotai'
import type { Poi } from '@/js/types'
import { poisAtom } from './mapAtoms.ts'
import { GeometryObject } from 'geojson'

type PoiEditorState = {
    activePoiId: string | number | null
    drafts: Record<string, Poi>
    dirtyPoiIds: string[]
    isSaving: boolean
    saveError: Error | null
}

type PoiDraftUpdate = Partial<Poi> | ((poi: Poi) => Poi)

const initialPoiEditorState: PoiEditorState = {
    activePoiId: null,
    drafts: {},
    dirtyPoiIds: [],
    isSaving: false,
    saveError: null,
}

const poiEditorStateAtom = atom<PoiEditorState>(initialPoiEditorState)

const getPoiKey = (poiId: string | number) => String(poiId)

export const activePoiIdAtom = atom((get) => get(poiEditorStateAtom).activePoiId)

// --- Poi Drafts ---
export const poiDraftAtom = atom((get) => {
    const state = get(poiEditorStateAtom)

    if (state.activePoiId === null) {
        return null
    }

    return state.drafts[getPoiKey(state.activePoiId)] ?? null
})

export const poiDirtyAtom = atom((get) => {
    const state = get(poiEditorStateAtom)

    if (state.activePoiId === null) {
        return false
    }

    return state.dirtyPoiIds.includes(getPoiKey(state.activePoiId))
})

export const dirtyPoiIdsAtom = atom((get) => {
    return new Set<string>(get(poiEditorStateAtom).dirtyPoiIds)
})

// --- Poi Saving ---
export const poiSavingAtom = atom((get) => get(poiEditorStateAtom).isSaving)

export const setPoiSavingAtom = atom(null, (_get, set, isSaving: boolean) => {
    set(poiEditorStateAtom, (state) => ({
        ...state,
        isSaving,
    }))
})

export const setPoiSaveErrorAtom = atom(null, (_get, set, error: Error | null) => {
    set(poiEditorStateAtom, (state) => ({
        ...state,
        saveError: error,
    }))
})

// --- Active Poi ---
export const canonicalActivePoiAtom = atom<Poi | null>((get) => {
    const activePoiId = get(activePoiIdAtom)

    if (activePoiId === null) {
        return null
    }

    return get(poisAtom).find((poi) => String(poi.id) === String(activePoiId)) ?? null
})

export const effectiveActivePoiAtom = atom<Poi | null>((get) => {
    const activePoiId = get(activePoiIdAtom)

    if (activePoiId === null) {
        return null
    }

    return get(poiDraftAtom) ?? get(poisAtom).find((poi) => String(poi.id) === String(activePoiId)) ?? null
})

// --- Actions ---
export const selectPoiAtom = atom(null, (get, set, poiId: string | number | null) => {
    if (poiId === null) {
        set(poiEditorStateAtom, (state) => ({
            ...state,
            activePoiId: null,
            saveError: null,
        }))

        return
    }

    const poi = get(poisAtom).find((candidate) => String(candidate.id) === String(poiId))

    if (!poi) {
        return
    }

    const state = get(poiEditorStateAtom)
    const poiKey = getPoiKey(poi.id)

    set(poiEditorStateAtom, {
        ...state,
        activePoiId: poi.id,
        drafts: state.drafts[poiKey]
            ? state.drafts
            : {
                  ...state.drafts,
                  [poiKey]: { ...poi },
              },
        saveError: null,
    })
})

export const updatePoiDraftAtom = atom(null, (get, set, update: PoiDraftUpdate) => {
    const currentPoi = get(effectiveActivePoiAtom)

    if (!currentPoi) {
        return
    }

    const draft =
        typeof update === 'function'
            ? update(currentPoi)
            : {
                  ...currentPoi,
                  ...update,
              }

    const state = get(poiEditorStateAtom)
    const poiKey = getPoiKey(currentPoi.id)

    set(poiEditorStateAtom, {
        ...state,
        activePoiId: currentPoi.id,
        drafts: {
            ...state.drafts,
            [poiKey]: draft,
        },
        dirtyPoiIds: state.dirtyPoiIds.includes(poiKey) ? state.dirtyPoiIds : [...state.dirtyPoiIds, poiKey],
        saveError: null,
    })
})

// --- Geometry Actions ---
export const addPoiGeometryAtom = atom(null, (get, set, geometry: GeometryObject) => {
    const poi = get(effectiveActivePoiAtom)

    if (!poi) {
        return
    }

    set(updatePoiDraftAtom, {
        geometries: {
            type: 'GeometryCollection',
            geometries: [...(poi.geometries?.geometries ?? []), geometry],
        },
    })
})

export const updatePoiGeometryAtom = atom(
    null,
    (
        get,
        set,
        {
            index,
            geometry,
        }: {
            index: number
            geometry: GeometryObject
        }
    ) => {
        const poi = get(effectiveActivePoiAtom)

        if (!poi) {
            return
        }

        const geometries = [...(poi.geometries?.geometries ?? [])]

        if (index < 0 || index >= geometries.length) {
            return
        }

        geometries[index] = geometry

        set(updatePoiDraftAtom, {
            geometries: {
                type: 'GeometryCollection',
                geometries,
            },
        })
    }
)

export const removePoiGeometryAtom = atom(null, (get, set, index: number) => {
    const poi = get(effectiveActivePoiAtom)

    if (!poi) {
        return
    }

    set(updatePoiDraftAtom, {
        geometries: {
            type: 'GeometryCollection',
            geometries: (poi.geometries?.geometries ?? []).filter(
                (_geometry, geometryIndex) => geometryIndex !== index
            ),
        },
    })
})

export const discardPoiDraftAtom = atom(null, (get, set) => {
    const activePoiId = get(activePoiIdAtom)

    if (activePoiId === null) {
        return
    }

    const state = get(poiEditorStateAtom)
    const poiKey = getPoiKey(activePoiId)
    const { [poiKey]: _discardedDraft, ...remainingDrafts } = state.drafts

    set(poiEditorStateAtom, {
        ...state,
        drafts: remainingDrafts,
        dirtyPoiIds: state.dirtyPoiIds.filter((id) => id !== poiKey),
        saveError: null,
    })
})

export const clearPoiEditorAtom = atom(null, (_get, set) => {
    set(poiEditorStateAtom, initialPoiEditorState)
})

export const markPoiSavedAtom = atom(null, (_get, set, savedPoi: Poi) => {
    set(poiEditorStateAtom, (state) => {
        const poiKey = getPoiKey(savedPoi.id)
        const { [poiKey]: _savedDraft, ...remainingDrafts } = state.drafts

        return {
            ...state,
            drafts: remainingDrafts,
            dirtyPoiIds: state.dirtyPoiIds.filter((id) => id !== poiKey),
            isSaving: false,
            saveError: null,
        }
    })
})
